import { useCallback } from 'react';
import useAnalyticsClient from '../client/use-analytics-client';
import { getClassContext } from './class-context';

function resolveAssignmentType(props) {
  if (props.type === 'assign' && props.previouslyHadResult) {
    return 'reassign';
  }
  return props.type;
}

export default function useTracking() {
  const client = useAnalyticsClient();

  const track = useCallback(
    (name, props, providerProps) => {
      if (!client) return;
      const eventProps = { ...getClassContext(), ...props };
      if (providerProps) {
        client.track(name, eventProps, providerProps);
      } else {
        client.track(name, eventProps);
      }
    },
    [client]
  );

  const trackAssignmentFlowStarted = useCallback(
    (props) => {
      // Every prop is always sent; null means not known when the flow started.
      track('assignment.flow-started', {
        type: resolveAssignmentType(props),
        target: props.target,
        source: props.source || null,
        action: props.action || null,
        activity_count: props.activityCount,
        student_count: props.studentCount,
        previously_had_result:
          props.previouslyHadResult !== undefined
            ? props.previouslyHadResult
            : null,
        previously_had_result_count:
          props.previouslyHadResultCount !== undefined
            ? props.previouslyHadResultCount
            : null,
        dueDate: props.dueDate || null
      });
    },
    [track]
  );

  const trackAssignmentConfirmed = useCallback(
    (props) => {
      track('assignment.confirmation', {
        type: resolveAssignmentType(props),
        target: props.target,
        ...(props.source ? { source: props.source } : {}),
        ...(props.action ? { action: props.action } : {}),
        activity_count: props.activityCount,
        student_count: props.studentCount,
        ...(props.previouslyHadResult !== undefined
          ? { previously_had_result: props.previouslyHadResult }
          : {}),
        ...(props.previouslyHadResultCount !== undefined
          ? { previously_had_result_count: props.previouslyHadResultCount }
          : {}),
        ...(props.durationMs !== undefined
          ? { duration_ms: props.durationMs }
          : {})
      });
    },
    [track]
  );

  const trackSwitchToOldAssign = useCallback(
    (props) => {
      track('switch.to-old-assign', {
        context: (props && props.context) || null
      });
    },
    [track]
  );

  // Group names can contain student names, so they go to teacher-totter only.
  const trackClassChanged = useCallback(
    (props) => {
      track(
        'filter.class-changed',
        {},
        { teacherTotter: { groupName: props.groupName || null } }
      );
    },
    [track]
  );

  const trackGroupChanged = useCallback(
    (props) => {
      track(
        'filter.group-changed',
        {},
        { teacherTotter: { groupName: props.groupName || null } }
      );
    },
    [track]
  );

  const trackTopicApplied = useCallback(
    (props) => {
      track('filter.topic-applied', { topic_count: props.topicCount });
    },
    [track]
  );

  const trackHowItWorks = useCallback(() => {
    track('ui.how-it-works', {});
  }, [track]);

  const trackViewKey = useCallback(() => {
    track('ui.view-key', {});
  }, [track]);

  const trackMenuAction = useCallback(
    (props) => {
      track('menu.action', { action: props.action });
    },
    [track]
  );

  const trackAccessChanged = useCallback(
    (props) => {
      track('access.changed', { restricted: props.restricted });
    },
    [track]
  );

  const trackActivityPreview = useCallback(
    (props) => {
      track('activity.preview', { activity_id: props.activityId });
    },
    [track]
  );

  const trackActivityTeacherNotes = useCallback(
    (props) => {
      track('activity.teacher-notes', { activity_id: props.activityId });
    },
    [track]
  );

  const trackStudentMenu = useCallback(
    (props) => {
      track('student.menu', { action: props.action });
    },
    [track]
  );

  return {
    trackAssignmentFlowStarted,
    trackAssignmentConfirmed,
    trackSwitchToOldAssign,
    trackClassChanged,
    trackGroupChanged,
    trackTopicApplied,
    trackHowItWorks,
    trackViewKey,
    trackMenuAction,
    trackAccessChanged,
    trackActivityPreview,
    trackActivityTeacherNotes,
    trackStudentMenu
  };
}



// WEBPACK FOOTER //
// ./analytics/tracking/use-tracking.js